import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Grid, IconButton, Paper, Skeleton, Tab, Tabs, Typography } from "@mui/material";
import { ArrowBack, Check, ContentCopy, FileDownload } from "@mui/icons-material";
import JsonView from "@uiw/react-json-view";
import { darkTheme } from "@uiw/react-json-view/dark";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { toast } from "react-toastify";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import VersionBadge from "../../lib/components/api/VersionBadge";
import transmissionService from "../../service/transmissionService";
import integrationService from "../../service/integrationService";

const CARD_SX = {
  borderRadius: "12px",
  border: "1px solid #E5E7EB",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  bgcolor: "#fff",
};

const JSON_THEME = {
  ...darkTheme,
  backgroundColor: "#0B1220",
  "--w-rjv-background-color": "#0B1220",
  "--w-rjv-color": "#E5E7EB",
  "--w-rjv-key-string": "#7DD3FC",
  "--w-rjv-type-string-color": "#4ADE80",
  "--w-rjv-type-int-color": "#4ADE80",
  "--w-rjv-type-float-color": "#4ADE80",
  "--w-rjv-type-number-color": "#4ADE80",
  "--w-rjv-type-boolean-color": "#4ADE80",
  "--w-rjv-type-null-color": "#9CA3AF",
  "--w-rjv-quotes-string-color": "#4ADE80",
  "--w-rjv-quotes-key-color": "#7DD3FC",
  "--w-rjv-curlybraces-color": "#E5E7EB",
  "--w-rjv-colon-color": "#9CA3AF",
  "--w-rjv-info-color": "#6B7280",
  "--w-rjv-arrow-color": "#9CA3AF",
};

const TABS = {
  JUREB: "jureb",
  NRS: "nrs",
};

function getEndpoint(url = "") {
  if (!url) return "/";
  try {
    return new URL(url).pathname || url;
  } catch {
    return String(url).split("?")[0] || url;
  }
}

function parseJson(value) {
  if (value == null || value === "") return {};
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return { value };
    }
  }
  return { value };
}

function pickFirst(...values) {
  return values.find((value) => value != null && value !== "");
}

function getStatusCode(log) {
  const code = Number(log?.response?.status || log?.response?.data?.statusCode || log?.response?.statusCode || log?.statusCode);
  if (Number.isFinite(code) && code > 0) return code;
  return log?.response?.data?.ok || (Number.isFinite(Number(log?.statusCode)) && Number(log?.statusCode) >= 200 && Number(log?.statusCode) < 300)
    ? 200
    : 500;
}

function toOperationLabel(url, explicit) {
  if (explicit) return String(explicit).replace(/\s+/g, "_").toUpperCase();
  const last = getEndpoint(url).split("/").filter(Boolean).pop() || "REQUEST";
  return last.replace(/-/g, "_").toUpperCase();
}

function unwrapLog(payload) {
  const body = payload?.data?.data ?? payload?.data ?? payload;
  if (body && typeof body === "object" && !Array.isArray(body) && (body.id || body.transportData)) return body;
  if (Array.isArray(body?.data)) return null;
  return body || null;
}

function getNested(source, path) {
  return path.split(".").reduce((acc, key) => (acc == null ? acc : acc[key]), source);
}

const ViewApiLogPage = () => {
  const { id, logId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const logFromState = location.state?.log;
  const [tab, setTab] = React.useState(TABS.JUREB);

  const integrationQuery = useQuery({
    enabled: Boolean(id),
    refetchOnWindowFocus: false,
    queryKey: ["integration-interface", id],
    queryFn: () => integrationService.getIntegrationsInterface({ interface: "FIRS", organizationId: id }),
  });

  const integration = integrationQuery.data?.data?.data;

  const logQuery = useQuery({
    enabled: Boolean(id) && Boolean(logId),
    queryKey: ["api-log", id, logId, integration?.id],
    placeholderData: logFromState,
    queryFn: async () => {
      try {
        const response = await transmissionService.getLog({
          organizationId: id,
          logId,
          integrationId: integration?.id,
        });
        const log = unwrapLog(response);
        if (log?.id || log?.transportData) return log;
        throw new Error("Log not found");
      } catch (error) {
        if (logFromState) return logFromState;
        const list = await transmissionService.getLogs({
          organizationId: id,
          integrationId: integration?.id,
          limit: 100,
        });
        const rows = list?.data?.data?.data || list?.data?.data || [];
        const match = (Array.isArray(rows) ? rows : []).find((row) => String(row?.id) === String(logId));
        if (!match) throw error;
        return match;
      }
    },
  });

  const log = logQuery.data || logFromState;
  const isLoading = logQuery.isPending && !log;

  const transport = log?.transportData || {};
  const requestBody = parseJson(pickFirst(log?.clientRequestBody, log?.requestBody, transport.data, transport.body, log?.request?.body, log?.requestBody));
  const responseBody = parseJson(pickFirst(log?.clientResponseBody, log?.responseBody, log?.response?.data, log?.response?.body));
  const nrsRequest = parseJson(
    pickFirst(
      log?.upstreamRequestBody,
      getNested(log, "upstream.requestBody"),
      getNested(log, "upstream.request"),
      getNested(log, "nrs.requestBody"),
      getNested(log, "nrs.request"),
      transport.upstreamRequestBody,
      transport.upstreamRequest,
      getNested(log, "response.data.upstreamRequest"),
      getNested(log, "firs.request"),
    ),
  );
  const nrsResponse = parseJson(
    pickFirst(
      log?.upstreamResponseBody,
      getNested(log, "upstream.responseBody"),
      getNested(log, "upstream.response"),
      getNested(log, "nrs.responseBody"),
      getNested(log, "nrs.response"),
      transport.upstreamResponseBody,
      transport.upstreamResponse,
      getNested(log, "response.data.upstream"),
      getNested(log, "firs.response"),
    ),
  );

  const endpoint = getEndpoint(transport.url || log?.endpoint);
  const method = String(transport.method || log?.method || "GET").toUpperCase();
  const statusCode = getStatusCode(log);
  const isSuccess = statusCode >= 200 && statusCode < 300;
  const durationMs = Number(log?.durationMs) || 0;
  const version = transport.version || transport.apiVersion || log?.apiVersion || null;
  const operation = toOperationLabel(transport.url || log?.endpoint, log?.action || log?.operation || transport.operation);
  const direction = String(log?.callDirection || log?.direction || transport.direction || "OUTBOUND_FIRS").replace(/\s+/g, "_").toUpperCase();
  const irn = pickFirst(
    log?.irn,
    getNested(log, "clientResponseBody.data.irn"),
    getNested(log, "clientRequestBody.irn"),
    requestBody.irn,
    requestBody.IRN,
    responseBody.irn,
    responseBody.IRN,
  );
  const idempotencyKey = pickFirst(
    log?.idempotencyKey,
    transport.idempotencyKey,
    transport.headers?.["idempotency-key"],
    transport.headers?.idempotencyKey,
    log?.headers?.["idempotency-key"],
  );
  const traceId = pickFirst(log?.requestId, log?.traceId, transport.requestId, transport.traceId, log?.id);
  const comment = pickFirst(
    log?.comment,
    log?.failureComment,
    log?.errorComment,
    getNested(log, "clientResponseBody.comment"),
    getNested(log, "clientResponseBody.message"),
    responseBody.comment,
    responseBody.message,
  );
  const timestamp = log?.createdAt ? moment(log.createdAt).format("ddd, DD MMM YYYY HH:mm:ss") : "N/A";

  const panels =
    tab === TABS.JUREB
      ? [
          { title: "Client Request Body", badge: "Jüreb Outbound", filename: "client-request-body.json", data: requestBody },
          { title: "Client Response Body", badge: "Jüreb Inbound", filename: "client-response-body.json", data: responseBody },
        ]
      : [
          { title: "NRS Request Body", badge: "NRS Outbound", filename: "nrs-request-body.json", data: nrsRequest },
          { title: "NRS Response Body", badge: "NRS Inbound", filename: "nrs-response-body.json", data: nrsResponse },
        ];

  return (
    <AppLayout>
      <Header title="FIRS Transmissions" description="Inspect request and response payloads" />
      <Box className="p-3 bg-light" sx={{ minHeight: "100%" }}>
        <IconButton className="bg-secondary-subtle" onClick={() => navigate(-1)} aria-label="Go back" sx={{ mb: 2 }}>
          <ArrowBack />
        </IconButton>

        <Paper variant="outlined" sx={{ ...CARD_SX, p: { xs: 2, md: 3 }, mb: 2.5 }}>
          {isLoading ? (
            <>
              <Skeleton width="70%" height={28} />
              <Skeleton width="50%" height={36} sx={{ my: 2 }} />
              <Skeleton height={72} />
            </>
          ) : (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap" mb={1.5}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <MetaBadge label={operation} variant="solid" color="#111827" />
                  {version ? <VersionBadge version={version} /> : null}
                  <MetaBadge label={method} variant="method"  />
                  <MetaBadge label={`HTTP ${statusCode}`} variant={isSuccess ? "success" : "danger"} showCheck={isSuccess} />
                  <MetaBadge label={direction} variant="muted" />
                </Box>
                <Box textAlign={{ xs: "left", sm: "right" }}>
                  <Typography fontSize={11} color="#9CA3AF" fontWeight={600} letterSpacing={0.4} textTransform="uppercase">
                    Timestamp
                  </Typography>
                  <Typography fontSize={14} fontWeight={700} color="#111827">
                    {timestamp}
                  </Typography>
                </Box>
              </Box>

              <Typography fontSize={20} fontWeight={700} color="#111827" mb={3} sx={{ wordBreak: "break-all" }}>
                {endpoint}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} lg={3}>
                  <SummaryField label="Invoice Ref Number (IRN)" value={irn || "N/A (Non-invoice endpoint)"} muted={!irn} />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <SummaryField label="Idempotency Key" value={idempotencyKey || "None supplied"} muted={!idempotencyKey} />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <SummaryField label="Trace / Request ID" value={traceId || "N/A"} muted copyable={Boolean(traceId)} />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <SummaryField label="Duration Breakdown" value={`${durationMs}ms Total`} strong>
                    <Box mt={1.25} height={8} borderRadius={4} bgcolor="#E5E7EB" overflow="hidden">
                      <Box
                        sx={{
                          width: durationMs ? "100%" : 0,
                          height: "100%",
                          bgcolor: "#2563EB",
                          borderRadius: 4,
                        }}
                      />
                    </Box>
                  </SummaryField>
                </Grid>
              </Grid>

              {comment ? (
                <Box mt={2}>
                  <SummaryField label="Comment" comment={comment} />
                </Box>
              ) : null}
            </>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ ...CARD_SX, p: { xs: 2, md: 3 } }}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
            <Typography fontSize={20} fontWeight={700} color="#111827">
              Request &amp; Response Payloads
            </Typography>
            <Box
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: "6px",
                bgcolor: "#F3F4F6",
                color: "#6B7280",
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              Client vs FIRS Upstream
            </Box>
          </Box>
          <Typography color="#6B7280" fontSize={14} mb={1.5}>
            Full payload inspection. Client and upstream bodies side-by-side.
          </Typography>

          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            sx={{
              minHeight: 40,
              mb: 2.5,
              "& .MuiTabs-indicator": { height: 3, bgcolor: "#2563EB" },
              "& .MuiTab-root": {
                textTransform: "none",
                minHeight: 40,
                fontWeight: 600,
                fontSize: 14,
                color: "#6B7280",
                px: 0,
                mr: 3,
              },
              "& .Mui-selected": { color: "#2563EB !important" },
            }}
          >
            <Tab value={TABS.JUREB} label="Jureb Request/Response" />
            <Tab value={TABS.NRS} label="NRS Request/Response" />
          </Tabs>

          {isLoading ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rounded" height={280} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rounded" height={280} />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={2}>
              {panels.map((panel) => (
                <Grid item xs={12} md={6} key={panel.title} sx={{ display: "flex" }}>
                  <PayloadPanel {...panel} />
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </Box>
    </AppLayout>
  );
};

const MetaBadge = ({ label, variant, color, showCheck }) => {
  const methodStyles = {
    GET: { bgcolor: "#E1F5FF", color: "#2563EB", border: "#93C5FD" },
    POST: { bgcolor: "#F5F3FF", color: "#7C3AED", border: "#C4B5FD" },
    PUT: { bgcolor: "#FFF7ED", color: "#EA580C", border: "#FDBA74" },
    PATCH: { bgcolor: "#FFF7ED", color: "#EA580C", border: "#FDBA74" },
    DELETE: { bgcolor: "#FEF2F2", color: "#EF4444", border: "#FCA5A5" },
  };
  const styles = {
    solid: { bgcolor: color, color: "#fff", border: `1px solid ${color}` },
    method: methodStyles[String(label).toUpperCase()] || methodStyles.GET,
    success: { bgcolor: "#DCFCE7", color: "#15803D", border: "1px solid #BBF7D0" },
    danger: { bgcolor: "#FEF2F2", color: "#B91C1C", border: "1px solid #FECACA" },
    muted: { bgcolor: "#F3F4F6", color: "#4B5563", border: "1px solid #E5E7EB" },
  };

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1.1,
        py: 0.4,
        borderRadius: "999px",
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.2,
        ...styles[variant],
        ...(variant === "method" ? { border: `1px solid ${styles.method.border}` } : {}),
      }}
    >
      {showCheck ? <Check sx={{ fontSize: 14 }} /> : null}
      {label}
    </Box>
  );
};

const SummaryField = ({ label, value, comment, muted, strong, copyable, children }) => {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value ?? comment ?? ""));
      toast.success("Copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "#F8F9FA",
        borderRadius: "10px",
        p: 1.75,
        height: "100%",
        minHeight: 88,
      }}
    >
      <Typography fontSize={11} color="#9CA3AF" fontWeight={700} letterSpacing={0.4} textTransform="uppercase" mb={0.75}>
        {label}
      </Typography>
      {value != null && value !== "" ? (
        <Box display="flex" alignItems="center" gap={0.75}>
          <Typography fontSize={14} fontWeight={strong ? 700 : 500} color={muted ? "#6B7280" : "#111827"} sx={{ wordBreak: "break-all" }}>
            {value}
          </Typography>
          {copyable ? (
            <IconButton size="small" onClick={handleCopy} aria-label="Copy value" sx={{ color: "#9CA3AF", p: 0.25 }}>
              <ContentCopy sx={{ fontSize: 16 }} />
            </IconButton>
          ) : null}
        </Box>
      ) : null}
      {comment ? (
        <Typography
          fontSize={14}
          fontWeight={500}
          color="#374151"
          mt={value != null && value !== "" ? 0.75 : 0}
          sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}
        >
          {comment}
        </Typography>
      ) : null}
      {children}
    </Box>
  );
};

const PayloadPanel = ({ title, badge, filename, data }) => {
  const json = data && typeof data === "object" ? data : {};

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(json, null, 2));
      toast.success("Payload copied");
    } catch {
      toast.error("Could not copy payload");
    }
  };

  const handleDownload = () => {
    const blob = new Blob([JSON.stringify(json, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box
      sx={{
        borderRadius: "12px",
        overflow: "hidden",
        bgcolor: "#0B1220",
        minHeight: 280,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        sx={{ px: 1.75, py: 1.25, bgcolor: "#111827" }}
      >
        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
          <Typography color="#E5E7EB" fontFamily="monospace" fontSize={13} fontWeight={700}>
            {"</>"}
          </Typography>
          <Typography color="#fff" fontSize={13} fontWeight={700} noWrap>
            {title}
          </Typography>
          <Box
            sx={{
              px: 1,
              py: 0.2,
              borderRadius: "6px",
              bgcolor: "#1F2937",
              color: "#D1D5DB",
              fontSize: 11,
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            {badge}
          </Box>
        </Box>
        <Box display="flex" alignItems="center">
          <IconButton size="small" onClick={handleCopy} aria-label="Copy payload" sx={{ color: "#9CA3AF" }}>
            <ContentCopy sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton size="small" onClick={handleDownload} aria-label="Download payload" sx={{ color: "#9CA3AF" }}>
            <FileDownload sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ px: 2, py: 2, overflow: "auto", maxHeight: 420, flex: 1, minHeight: 0 }}>
        <JsonView
          value={json}
          style={JSON_THEME}
          displayDataTypes={false}
          enableClipboard={false}
          shortenTextAfterLength={0}
        />
      </Box>
    </Box>
  );
};

export default ViewApiLogPage;
